import { useRef, useState } from "react";
import { toast } from "sonner";
import { Siren, X, PhoneCall } from "lucide-react";
import api from "../../lib/api";

/* Press-and-hold SOS. Hold 1.5s to arm, pick a category, confirm to send. */
const CATEGORIES = [
  ["medical", "Medical emergency"], ["safety", "Personal safety"], ["lost", "Lost or stranded"],
  ["vehicle", "Vehicle problem"], ["missed_transport", "Missed transportation"],
  ["documents", "Passport / document loss"], ["family_contact", "Need family contact"], ["other", "Other"],
];

export default function GuardianSOS() {
  const [armed, setArmed] = useState(false);
  const [category, setCategory] = useState("other");
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const holdTimer = useRef(null);
  const [holding, setHolding] = useState(false);

  const startHold = () => {
    setHolding(true);
    holdTimer.current = setTimeout(() => { setArmed(true); setHolding(false); }, 1500);
  };
  const endHold = () => { clearTimeout(holdTimer.current); setHolding(false); };

  const send = () => {
    setBusy(true);
    const fire = async (lat, lon) => {
      try {
        const r = await api.post("/guardian/sos", { category, note, lat, lon, country: "" });
        setResult(r.data); setArmed(false);
        toast.success(`SOS sent — ${r.data.contacts_notified} contact(s) notified`);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not send SOS"); }
      setBusy(false);
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => fire(p.coords.latitude, p.coords.longitude),
        () => fire(null, null), { timeout: 8000 });
    } else fire(null, null);
  };

  const cancelSos = async () => {
    try {
      await api.post(`/guardian/sos/${result.alert_id}/cancel`);
      toast.success("SOS cancelled — your circle was told all is OK");
      setResult(null);
    } catch { toast.error("Could not cancel"); }
  };

  if (result) {
    return (
      <div className="border-2 border-rose-300 bg-rose-50 rounded-2xl p-5" data-testid="guardian-sos-sent">
        <h3 className="font-heading font-900 text-base md:text-lg text-rose-800 flex items-center gap-2"><Siren className="w-5 h-5" /> SOS sent — {result.contacts_notified} contact(s) notified</h3>
        <p className="mt-1 text-xs text-rose-700">{result.disclaimer}</p>
        <div className="mt-3">
          <div className="text-[11px] font-800 uppercase text-rose-700 mb-1.5">Local emergency numbers</div>
          <div className="flex flex-wrap gap-2" data-testid="guardian-sos-numbers">
            {result.emergency_numbers.map((n) => (
              <a key={n.number} href={`tel:${n.number}`} className="inline-flex items-center gap-1.5 bg-white border border-rose-300 text-rose-800 rounded-full px-4 py-2 text-sm font-900 hover:bg-rose-100">
                <PhoneCall className="w-4 h-4" /> {n.label}: {n.number}
              </a>
            ))}
          </div>
        </div>
        <button onClick={cancelSos} data-testid="guardian-sos-cancel"
          className="mt-4 inline-flex items-center gap-1.5 border border-rose-400 text-rose-700 text-xs font-800 rounded-full px-4 py-2 hover:bg-rose-100"><X className="w-3.5 h-3.5" /> False alarm — cancel & tell everyone I'm OK</button>
      </div>
    );
  }

  if (armed) {
    return (
      <div className="border-2 border-rose-300 bg-rose-50 rounded-2xl p-5" data-testid="guardian-sos-armed">
        <h3 className="font-heading font-800 text-base text-rose-800">What kind of help do you need?</h3>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CATEGORIES.map(([v, l]) => (
            <button key={v} onClick={() => setCategory(v)} data-testid={`guardian-sos-cat-${v}`}
              className={`text-xs font-700 rounded-xl px-2 py-2.5 border ${category === v ? "bg-rose-600 text-white border-rose-600" : "bg-white border-rose-200 text-rose-800 hover:border-rose-400"}`}>{l}</button>
          ))}
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="guardian-sos-note"
          placeholder="Optional message to your contacts…" className="mt-3 w-full border border-rose-300 rounded-xl px-3 py-2.5 text-sm bg-white" />
        <div className="mt-3 flex gap-2">
          <button onClick={send} disabled={busy} data-testid="guardian-sos-confirm"
            className="bg-rose-600 text-white text-sm font-900 rounded-full px-6 py-3 hover:bg-rose-700 disabled:opacity-50">SEND SOS NOW</button>
          <button onClick={() => setArmed(false)} data-testid="guardian-sos-abort" className="text-sm font-700 text-zinc-500 px-3">Cancel</button>
        </div>
        <p className="mt-2 text-[11px] text-rose-700">Your current (or last-known) location will be shared with everyone in your circles.</p>
      </div>
    );
  }

  return (
    <button
      onMouseDown={startHold} onMouseUp={endHold} onMouseLeave={endHold}
      onTouchStart={startHold} onTouchEnd={endHold}
      data-testid="guardian-sos-btn"
      className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-900 text-white transition-transform ${holding ? "bg-rose-700 scale-95" : "bg-rose-600 hover:bg-rose-700"}`}>
      <Siren className="w-4 h-4" /> {holding ? "KEEP HOLDING…" : "SOS — HOLD"}
    </button>
  );
}
